import * as React from "react";

import { cva, type VariantProps } from "class-variance-authority";

import { H3, P } from "~/components/ui/typography";
import { cn } from "~/lib/utils";

// AIDEV-NOTE: Card variant gradients updated to brand colors (forest/terra)
// AIDEV-NOTE: Reduced vertical padding on mobile (py-3 vs p-6)
// Define card variants using cva
const cardVariants = cva("p-6 py-3 md:py-6 mb-4 border rounded-lg", {
    variants: {
        variant: {
            default: "border-border bg-card text-card-foreground shadow-sm",
            gradient: "border-transparent bg-forest-gradient", // Gradient background will be handled by gradientColor
            // AIDEV-NOTE: "crafted" — soft premium card from Session Notes redesign
            // mockup 2: white surface, hairline forest-100 border, larger radius,
            // soft forest-tinted lift (shadow-card token). Used by part cards +
            // commitments.
            crafted:
                "border-brand-forest-100 bg-white text-card-foreground rounded-[18px] shadow-card",
        },
        gradientColor: {
            none: "",
            rose: "bg-forest-gradient",
            orange: "bg-terra-gradient",
        },
    },
    defaultVariants: {
        variant: "default",
        gradientColor: "none",
    },
});

// Update CardProps to use VariantProps
interface CardProps
    extends React.HTMLAttributes<HTMLDivElement>,
        VariantProps<typeof cardVariants> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
    ({ className, variant, gradientColor, ...props }, ref) => (
        <div
            ref={ref}
            // Apply variants using cn and cardVariants
            className={cn(cardVariants({ variant, gradientColor, className }))}
            {...props}
        />
    ),
);
Card.displayName = "Card";

const CardHeader = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
    <div
        ref={ref}
        className={cn("flex flex-col space-y-1.5 p-6", className)}
        {...props}
    />
));
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef<
    HTMLHeadingElement,
    React.HTMLAttributes<HTMLHeadingElement>
>(({ className, children, ...props }, ref) => (
    <div ref={ref}>
        <H3
            className={cn(
                "text-2xl font-semibold leading-relaxed tracking-tight",
                className,
            )}
            {...props}
        >
            {children}
        </H3>
    </div>
));
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<
    HTMLParagraphElement,
    React.HTMLAttributes<HTMLParagraphElement>
>(({ className, children, ...props }, ref) => (
    <div ref={ref}>
        <P
            className={cn("text-sm text-muted-foreground", className)}
            {...props}
        >
            {children}
        </P>
    </div>
));
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
    <div ref={ref} className={cn("p-6 pt-0 mt-0", className)} {...props} />
));
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
    <div
        ref={ref}
        className={cn("flex items-center p-6 pt-0", className)}
        {...props}
    />
));
CardFooter.displayName = "CardFooter";

export {
    Card,
    CardHeader,
    CardFooter,
    CardTitle,
    CardDescription,
    CardContent,
};
